import { vec3 } from 'gl-matrix';
import { Subject } from 'rxjs';

export class TimedMovement<TPath extends PositionObject> {
  public onStep: Subject<Step>;
  public onComplete: Subject<TimedMovement<TPath>>;

  public isActive: boolean = false;
  private path: TPath[];
  private lastStep!: Date;

  constructor(
    public positionObject: PositionObject,
    initialNode: TPath,
    public speed: number,
  ) {
    this.onStep = new Subject<Step>();
    this.onComplete = new Subject<TimedMovement<TPath>>();

    this.path = [initialNode];
  }

  public get currentNode(): TPath | null {
    return this.path.length > 0 ? this.path[0] : null;
  }

  /**
   * Moves the object along the configured path at the given speed. Calling step frequently
   * ensures smooth transitioning between positions.
   */
  public step() {
    if (!this.isActive) {
      return;
    }

    let currentPosition: vec3 = this.positionObject.position as vec3;

    let elapsedTime = new Date().getTime() - this.lastStep.getTime();
    this.lastStep = new Date();

    if (elapsedTime == 0) {
      // When no time elapsed then no progress has been made.
      return;
    }

    let oldPosition = vec3.clone(currentPosition);

    if (!this.travel(elapsedTime * (this.speed / 1000))) {
      // Path is not yet completed, so emit step event.
      this.onStep.next({
        oldPosition: oldPosition,
        newPosition: currentPosition,
      });
    } else {
      // No path left, we have arrived at the final destination.
      this.isActive = false;
      this.onComplete.next(this);
    }
  }

  /**
   * Moves the object a given distance along the path.
   * @param travelDistance the distance to travel within the current path
   * @returns whether the path was completed.
   */
  private travel(travelDistance: number): boolean {
    if (travelDistance == 0) {
      return false;
    }

    let currentPosition: vec3 = this.positionObject.position as vec3;
    let currentNodePosition = this.currentNode?.position as number[];

    if (!currentNodePosition) {
      // No current node, so nothing to do.
      return true;
    }

    // Y-up: ground-plane distance uses X (index 0) and Z (index 2)
    let dx = currentPosition[0] - (currentNodePosition[0] as number);
    let dz = currentPosition[2] - (currentNodePosition[2] as number);
    let goalDistance = Math.sqrt(dx * dx + dz * dz);

    if (travelDistance > goalDistance) {
      // Moved further than distance to current goal, so set XZ to goal location.
      // Y is managed by physics/navigation (VolumeGrid floor height).
      currentPosition[0] = currentNodePosition[0];
      currentPosition[2] = currentNodePosition[2];

      if (this.path.length > 1) {
        this.path.shift();
        // Recursively travel the remaining distance.
        return this.travel(travelDistance - goalDistance);
      } else {
        // No path left, we have arrived at the final destination.
        return true;
      }
    } else {
      // Travel the required distance in a straight line on the XZ ground plane.
      let ratio = travelDistance / goalDistance;

      let diff = vec3.sub(vec3.create(), currentNodePosition, currentPosition);
      vec3.multiply(diff, diff, [ratio, 0, ratio]);
      vec3.add(currentPosition, currentPosition, diff);

      return false;
    }
  }

  /**
   * Sets the movement path for the object.
   * @param pathNodes the nodes of the path the object should be moved along
   */
  public setPath(pathNodes: TPath[]) {
    //this.path = pathNodes.slice();

    if (pathNodes.length > 0) {
      this.path.length = 1;

      this.path.push(...pathNodes);
      this.isActive = true;
      this.lastStep = new Date();
    }
  }

  public clear() {
    this.isActive = false;
    this.path.length = 1;
  }

  public setCurrentNode(node: TPath) {
    this.path[0] = node;
  }

  public getActivePath(): TPath[] {
    if (this.isActive) {
      return this.path.slice(1);
    } else {
      return [];
    }
  }
}

type PositionObject = {
  position: vec3 | number[];
};

type Step = {
  oldPosition: vec3;
  newPosition: vec3;
};
